/**
 * Shared Comparison Utilities
 *
 * Common functions for comparing field values across records.
 * Used by both useSplicingDiff and useFieldComparison hooks.
 */

import {
  FieldMetadata,
  FieldCategory,
  showInComparison,
} from '@/services/metadataService'

/**
 * System fields that should be excluded from comparison by default.
 * These are internal tracking fields that aren't meaningful to users.
 *
 * @deprecated Use shouldExcludeFromComparison() with category-based filtering instead.
 *             This set is kept for backward compatibility.
 */
export const DEFAULT_SYSTEM_FIELDS = new Set([
  'uid',
  'orgId',
  'keyComboHash',
  'lifecycleState',
  'lifecycleVersion',
  'effectiveFrom',
  'effectiveTo',
  'activeFrom',
  'activeTo',
  'supersededByUid',
  'version',
  'createdAt',
  'createdBy',
  'modifiedAt',
  'modifiedBy',
  'deletedAt',
  'deletedBy',
  'submittedAt',
  'submittedBy',
  'approvedAt',
  'approvedBy',
  'externalId',
  'connectionId',
])

/**
 * Categories that should be excluded from comparison views.
 *
 * These field categories contain system/internal data that users
 * don't need to see when comparing records.
 */
export const EXCLUDED_COMPARISON_CATEGORIES: Set<FieldCategory> = new Set([
  'identity',
  'tenant_context',
  'computed',
  'soft_delete',
])

/**
 * Check if a field should be excluded from comparison based on category.
 *
 * Uses category-based system if metadata is available, falls back to
 * DEFAULT_SYSTEM_FIELDS set for backward compatibility.
 *
 * @param fieldKey - The field key/name
 * @param metadata - Optional field metadata with category information
 * @returns True if field should be excluded from comparison
 */
export function shouldExcludeFromComparison(
  fieldKey: string,
  metadata?: FieldMetadata
): boolean {
  // If we have metadata with category info, use category-based filtering
  if (metadata) {
    return !showInComparison(metadata)
  }

  // Fall back to hardcoded set for backward compatibility
  return DEFAULT_SYSTEM_FIELDS.has(fieldKey)
}

/**
 * Filter fields to show only comparison-relevant fields.
 *
 * @param fields - Record of field name to metadata
 * @returns Filtered record with only comparison-relevant fields
 */
export function filterComparisonFields(
  fields: Record<string, FieldMetadata>
): Record<string, FieldMetadata> {
  const result: Record<string, FieldMetadata> = {}

  for (const [key, metadata] of Object.entries(fields)) {
    if (!shouldExcludeFromComparison(key, metadata)) {
      result[key] = metadata
    }
  }

  return result
}

/** Floating point comparison epsilon */
const FLOAT_EPSILON = 0.0000001

/**
 * Check if a value is numeric (number or numeric string).
 */
export function isNumeric(value: unknown): value is number {
  if (typeof value === 'number' && !isNaN(value)) {
    return true
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return !isNaN(parsed) && isFinite(parsed)
  }
  return false
}

/**
 * Convert value to number for comparison.
 */
export function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && !isNaN(value)) {
    return value
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    if (!isNaN(parsed) && isFinite(parsed)) {
      return parsed
    }
  }
  return null
}

/**
 * Deep equality check for comparing field values.
 * Handles primitives, arrays, objects, dates, null/undefined.
 */
export function areValuesEqual(a: unknown, b: unknown): boolean {
  // Handle null/undefined
  if (a === null || a === undefined) {
    return b === null || b === undefined
  }
  if (b === null || b === undefined) {
    return false
  }

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }
  if (a instanceof Date || b instanceof Date) {
    const aStr = a instanceof Date ? a.toISOString() : String(a)
    const bStr = b instanceof Date ? b.toISOString() : String(b)
    return aStr === bStr
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((val, idx) => areValuesEqual(val, b[idx]))
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return false
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a as object)
    const bKeys = Object.keys(b as object)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every(key =>
      areValuesEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
    )
  }

  // Handle numbers (with tolerance for floating point)
  if (typeof a === 'number' && typeof b === 'number') {
    if (a === b) return true
    return Math.abs(a - b) < FLOAT_EPSILON
  }

  // Handle string/number coercion (e.g., "100" vs 100)
  if (typeof a === 'string' && typeof b === 'number') {
    const aNum = toNumber(a)
    return aNum !== null && Math.abs(aNum - b) < FLOAT_EPSILON
  }
  if (typeof a === 'number' && typeof b === 'string') {
    const bNum = toNumber(b)
    return bNum !== null && Math.abs(a - bNum) < FLOAT_EPSILON
  }

  // Primitive comparison
  return a === b
}

/**
 * Calculate numeric delta and percentage change between two values.
 *
 * @param newValue - The new/anchor value
 * @param baselineValue - The baseline/comparison value
 * @returns Object with numericDelta and percentDelta (if calculable)
 */
export function calculateNumericDelta(
  newValue: unknown,
  baselineValue: unknown
): { numericDelta?: number; percentDelta?: number } {
  const newNum = toNumber(newValue)
  const baselineNum = toNumber(baselineValue)

  if (newNum === null || baselineNum === null) {
    return {}
  }

  const numericDelta = newNum - baselineNum

  // Calculate percent delta (relative to baseline value)
  let percentDelta: number | undefined
  if (baselineNum !== 0) {
    percentDelta = ((newNum - baselineNum) / Math.abs(baselineNum)) * 100
  } else if (newNum !== 0) {
    // If baseline is 0 and new is not, it's 100% increase
    percentDelta = 100
  }

  return { numericDelta, percentDelta }
}

/**
 * Get the field title from metadata, with fallback to formatted field key.
 */
export function getFieldTitle(
  fieldKey: string,
  metadata?: { title?: string | null }
): string {
  if (metadata?.title) {
    return metadata.title
  }

  // Convert camelCase to Title Case
  return fieldKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}
